import {
	getAvailableIntegrations,
	enableIntegration,
	disableIntegration,
	updateIntegrationCredentials,
	testIntegrationConnection,
	type IntegrationInfo,
} from "@/services/integration-management-service";
import { useAdminStore } from "@/stores/admin-store";
import { UserAccessLevel, useUserStore } from "@/stores/user-store";
import {
	ApiOutlined,
	CheckCircleOutlined,
	ClockCircleOutlined,
	ControlOutlined,
	DashboardOutlined,
	KeyOutlined,
	LinkOutlined,
	LoadingOutlined,
	SyncOutlined,
	ToolOutlined,
} from "@ant-design/icons";
import {
	Button,
	Card,
	Col,
	Form,
	Input,
	Modal,
	Row,
	Spin,
	Switch,
	Tag,
	Tooltip,
	Typography,
	message,
} from "antd";
import { useCallback, useEffect, useState } from "react";
import { SyncConfigModal } from "./SyncConfigModal";
import { SyncDashboardModal } from "./SyncDashboardModal";
import "./IntegrationsTab.scss";

const { Title, Text, Paragraph } = Typography;

// Integration SVG logos
const IntegrationLogo = ({ name, color }: { name: string; color: string }) => (
	<svg
		width="48"
		height="48"
		viewBox="0 0 48 48"
		fill="none"
		xmlns="http://www.w3.org/2000/svg"
	>
		<rect width="48" height="48" rx="10" fill={color} />
		<text
			x="50%"
			y="50%"
			dominantBaseline="middle"
			textAnchor="middle"
			fill="white"
			fontFamily="Arial, sans-serif"
			fontSize={name.length > 2 ? "13" : "18"}
			fontWeight="bold"
		>
			{name}
		</text>
	</svg>
);

// Integration brand colors and abbreviations
const INTEGRATION_BRANDING: Record<number, { abbr: string; color: string; tier: string }> = {
	// CMMS
	1: { abbr: "SC", color: "#0066CC", tier: "Tier 1" },
	2: { abbr: "ET", color: "#2DB84B", tier: "Tier 1" },
	4: { abbr: "LMB", color: "#00A651", tier: "Tier 1" },
	5: { abbr: "MX", color: "#4A90D9", tier: "Tier 1" },
	6: { abbr: "UK", color: "#FF6B35", tier: "Tier 1" },
	// IoT / Sensors
	7: { abbr: "TA", color: "#E53935", tier: "IoT" },
	8: { abbr: "SAM", color: "#1A1A2E", tier: "IoT" },
	9: { abbr: "MON", color: "#0288D1", tier: "IoT" },
};

// Integration categories
const INTEGRATION_CATEGORIES: Record<number, string> = {
	1: "Facility Management",
	2: "Facility Management",
	4: "CMMS",
	5: "CMMS",
	6: "CMMS",
	7: "IoT Sensors",
	8: "IoT Sensors",
	9: "IoT Sensors",
};

// IDs that belong to the IoT section
const IOT_INTEGRATION_IDS = new Set([7, 8, 9]);

interface CredentialModalState {
	visible: boolean;
	integrationId: number | null;
	integrationName: string;
	authSchema: any;
}

const IntegrationsTab = () => {
	const { user } = useUserStore();
	const {
		setIsIntegrationEnabled,
		setIntegrationCheckedForUserId,
		setIntegrationCheckedForOrgId,
	} = useAdminStore();

	const [loading, setLoading] = useState(true);
	const [integrations, setIntegrations] = useState<IntegrationInfo[]>([]);
	const [togglingId, setTogglingId] = useState<number | null>(null);
	const [testingId, setTestingId] = useState<number | null>(null);
	const [credentialModal, setCredentialModal] = useState<CredentialModalState>({
		visible: false,
		integrationId: null,
		integrationName: "",
		authSchema: null,
	});
	const [savingCredentials, setSavingCredentials] = useState(false);
	const [syncConfigModal, setSyncConfigModal] = useState<{
		visible: boolean;
		integrationId: number;
		integrationName: string;
	}>({ visible: false, integrationId: 0, integrationName: "" });
	const [syncDashboardModal, setSyncDashboardModal] = useState<{
		visible: boolean;
		integrationId: number;
		integrationName: string;
	}>({ visible: false, integrationId: 0, integrationName: "" });
	const [form] = Form.useForm();

	const userAccessLevel =
		user?.role?.accessLevel ?? user?.role?.access_level ?? 0;
	const isSuperAdmin = userAccessLevel >= UserAccessLevel.SUPER_ADMIN;

	// Fetch integrations from the API
	const fetchIntegrations = useCallback(async () => {
		try {
			setLoading(true);
			const data = await getAvailableIntegrations();
			setIntegrations(data);
		} catch (error) {
			console.error("Error fetching integrations:", error);
			message.error("Failed to load integrations");
		} finally {
			setLoading(false);
		}
	}, []);

	useEffect(() => {
		if (user) {
			fetchIntegrations();
		}
	}, [user, fetchIntegrations]);

	const handleToggle = async (integrationId: number, enabled: boolean) => {
		if (!isSuperAdmin) {
			message.warning("Only super admins can manage integrations");
			return;
		}

		try {
			setTogglingId(integrationId);

			if (enabled) {
				await enableIntegration(integrationId);
				message.success("Integration enabled");
			} else {
				await disableIntegration(integrationId);
				message.success("Integration disabled");
			}

			// Refresh data
			await fetchIntegrations();

			// Update admin store for ServiceChannel backward compatibility
			if (integrationId === 1) {
				setIsIntegrationEnabled(enabled);
				setIntegrationCheckedForUserId(undefined);
				setIntegrationCheckedForOrgId(undefined);
			}
		} catch (error: any) {
			message.error(error.message || "Failed to update integration");
		} finally {
			setTogglingId(null);
		}
	};

	const handleTestConnection = async (integration: IntegrationInfo) => {
		try {
			setTestingId(integration.id);
			const result = await testIntegrationConnection(integration.id);

			if (result.connected) {
				message.success(`Successfully connected to ${integration.name}`);
			} else {
				message.error(`Connection test failed for ${integration.name}`);
			}
		} catch (error: any) {
			message.error(error.message || "Connection test failed");
		} finally {
			setTestingId(null);
		}
	};

	const openCredentialModal = (integration: IntegrationInfo) => {
		setCredentialModal({
			visible: true,
			integrationId: integration.id,
			integrationName: integration.name,
			authSchema: integration.authSchema,
		});
		form.resetFields();
	};

	const handleSaveCredentials = async () => {
		try {
			const values = await form.validateFields();
			setSavingCredentials(true);

			await updateIntegrationCredentials(credentialModal.integrationId!, values);
			message.success("Credentials saved successfully");

			setCredentialModal({ visible: false, integrationId: null, integrationName: "", authSchema: null });
			form.resetFields();

			// Refresh integrations to update hasCredentials status
			await fetchIntegrations();
		} catch (error: any) {
			if (error.errorFields) return; // Form validation error
			message.error(error.message || "Failed to save credentials");
		} finally {
			setSavingCredentials(false);
		}
	};

	const formatConnectedDate = (dateString: string | null): string => {
		if (!dateString) return "Not connected";
		try {
			const date = new Date(dateString);
			return date.toLocaleDateString("en-US", {
				year: "numeric",
				month: "long",
				day: "numeric",
			});
		} catch {
			return "Unknown";
		}
	};

	// Split integrations into CMMS and IoT groups
	const cmmsIntegrations = integrations.filter((i) => !IOT_INTEGRATION_IDS.has(i.id));
	const iotIntegrations = integrations.filter((i) => IOT_INTEGRATION_IDS.has(i.id));

	const renderIntegrationCard = (integration: IntegrationInfo) => {
		const branding = INTEGRATION_BRANDING[integration.id] || {
			abbr: integration.name.substring(0, 2).toUpperCase(),
			color: "#8c8c8c",
			tier: "",
		};
		const category = INTEGRATION_CATEGORIES[integration.id] || "Integration";

		return (
			<Col xs={24} sm={24} md={12} lg={8} xl={6} key={integration.id}>
				<Card
					className={`integration-card ${integration.isEnabled ? "enabled" : "disabled"}`}
					hoverable
				>
					<div className="integration-card-content">
						<div className="integration-header">
							<div className="integration-logo-container">
								<IntegrationLogo name={branding.abbr} color={branding.color} />
							</div>
							<div className="integration-toggle">
								{isSuperAdmin ? (
									<Switch
										checked={integration.isEnabled}
										onChange={(checked) =>
											handleToggle(integration.id, checked)
										}
										loading={togglingId === integration.id}
									/>
								) : (
									<Tooltip title="Only super admins can manage integrations">
										<Switch
											checked={integration.isEnabled}
											disabled
										/>
									</Tooltip>
								)}
							</div>
						</div>

						<div className="integration-info">
							<div className="integration-title-row">
								<Title level={5} className="integration-name">
									{integration.name}
								</Title>
								<Tag color={integration.isEnabled ? "green" : "default"}>
									{integration.isEnabled ? "Connected" : "Not Connected"}
								</Tag>
							</div>

							<div className="integration-tags">
								<Tag color="blue" className="integration-category">
									{category}
								</Tag>
								{branding.tier && (
									<Tag color="purple" className="integration-tier">
										{branding.tier}
									</Tag>
								)}
							</div>

							<Paragraph
								type="secondary"
								className="integration-description"
								ellipsis={{ rows: 2 }}
							>
								{integration.description}
							</Paragraph>
						</div>

						<div className="integration-footer">
							<div className="integration-status">
								{integration.isEnabled ? (
									<>
										<CheckCircleOutlined className="status-icon connected" />
										<Text type="secondary" className="connected-since">
											<ClockCircleOutlined style={{ marginRight: 4 }} />
											Connected since{" "}
											{formatConnectedDate(integration.connectedAt)}
										</Text>
									</>
								) : (
									<>
										<ApiOutlined className="status-icon disconnected" />
										<Text type="secondary">
											Enable to start syncing data
										</Text>
									</>
								)}
							</div>

							{integration.isEnabled && (
								<div className="integration-actions">
									<div className="action-row">
										<Tooltip title={integration.hasCredentials ? "Update API credentials" : "Configure API credentials"}>
											<Button
												size="small"
												icon={<KeyOutlined />}
												onClick={() => openCredentialModal(integration)}
												className={`action-btn credential-btn ${!integration.hasCredentials ? "needs-setup" : ""}`}
											>
												{integration.hasCredentials ? "Credentials" : "Set Up"}
											</Button>
										</Tooltip>

										{integration.hasCredentials && (
											<Tooltip title="Configure sync directions and safety settings">
												<Button
													size="small"
													icon={<ControlOutlined />}
													onClick={() => setSyncConfigModal({
														visible: true,
														integrationId: integration.id,
														integrationName: integration.name,
													})}
													className="action-btn configure-btn"
												>
													Configure
												</Button>
											</Tooltip>
										)}
									</div>

									{integration.hasCredentials && (
										<div className="action-row">
											<Tooltip title="Run sync, view jobs, and manage mappings">
												<Button
													size="small"
													icon={<SyncOutlined />}
													onClick={() => setSyncDashboardModal({
														visible: true,
														integrationId: integration.id,
														integrationName: integration.name,
													})}
													className="action-btn sync-btn"
												>
													Sync
												</Button>
											</Tooltip>
										</div>
									)}
								</div>
							)}
						</div>
					</div>
				</Card>
			</Col>
		);
	};

	if (loading) {
		return (
			<div className="integrations-loading">
				<Spin size="large" />
			</div>
		);
	}

	return (
		<div className="integrations-container">
			<div className="integrations-header">
				<Title level={3}>Integrations</Title>
				<Text type="secondary">
					Connect FacilityAI with your CMMS platforms and IoT sensors to streamline
					your facility management workflow.
				</Text>
			</div>

			{/* CMMS Platforms Section */}
			<div className="integrations-section">
				<div className="integrations-section-header">
					<ToolOutlined className="section-icon" />
					<div>
						<Title level={4} className="section-title">CMMS Platforms</Title>
						<Text type="secondary">
							Sync assets, work orders, and locations with your maintenance management system.
						</Text>
					</div>
				</div>
				<Row gutter={[24, 24]} className="integrations-grid">
					{cmmsIntegrations.map(renderIntegrationCard)}
				</Row>
			</div>

			{/* IoT Sensors Section */}
			<div className="integrations-section">
				<div className="integrations-section-header">
					<DashboardOutlined className="section-icon" />
					<div>
						<Title level={4} className="section-title">IoT Sensors</Title>
						<Text type="secondary">
							Connect environmental sensors for real-time temperature, humidity, and equipment monitoring.
						</Text>
					</div>
				</div>
				<Row gutter={[24, 24]} className="integrations-grid">
					{iotIntegrations.map(renderIntegrationCard)}
				</Row>
			</div>

			{/* Credential Configuration Modal */}
			<Modal
				title={
					<div className="credential-modal-title">
						<KeyOutlined style={{ marginRight: 8, color: "#92278F" }} />
						{credentialModal.integrationName} Credentials
					</div>
				}
				open={credentialModal.visible}
				onCancel={() => {
					setCredentialModal({ visible: false, integrationId: null, integrationName: "", authSchema: null });
					form.resetFields();
				}}
				footer={[
					<Button
						key="test"
						icon={testingId === credentialModal.integrationId ? <LoadingOutlined /> : <LinkOutlined />}
						onClick={() => {
							const integration = integrations.find((i) => i.id === credentialModal.integrationId);
							if (integration) handleTestConnection(integration);
						}}
						loading={testingId === credentialModal.integrationId}
						disabled={!integrations.find((i) => i.id === credentialModal.integrationId)?.hasCredentials}
					>
						Test Connection
					</Button>,
					<Button
						key="cancel"
						onClick={() => {
							setCredentialModal({ visible: false, integrationId: null, integrationName: "", authSchema: null });
							form.resetFields();
						}}
					>
						Cancel
					</Button>,
					<Button
						key="save"
						type="primary"
						onClick={handleSaveCredentials}
						loading={savingCredentials}
						style={{ background: "#92278F", borderColor: "#92278F" }}
					>
						Save Credentials
					</Button>,
				]}
				width={480}
				destroyOnClose
			>
				<div className="credential-modal-body">
					<Paragraph type="secondary" style={{ marginBottom: 16 }}>
						Enter your API credentials for {credentialModal.integrationName}. 
						These will be securely stored and used to connect to the platform.
					</Paragraph>

					<Form form={form} layout="vertical">
						{credentialModal.authSchema?.fields?.map((field: any) => (
							<Form.Item
								key={field.key}
								name={field.key}
								label={field.label}
								rules={[
									{
										required: field.required,
										message: `${field.label} is required`,
									},
								]}
								extra={field.helpText && (
									<Text type="secondary" style={{ fontSize: 12 }}>
										{field.helpText}
									</Text>
								)}
							>
								{field.type === "password" ? (
									<Input.Password
										placeholder={`Enter your ${field.label.toLowerCase()}`}
										size="large"
									/>
								) : (
									<Input
										placeholder={`Enter your ${field.label.toLowerCase()}`}
										size="large"
									/>
								)}
							</Form.Item>
						))}
					</Form>
				</div>
			</Modal>

			{/* Sync Configuration Modal */}
			<SyncConfigModal
				open={syncConfigModal.visible}
				onClose={() => setSyncConfigModal({ visible: false, integrationId: 0, integrationName: "" })}
				integrationId={syncConfigModal.integrationId}
				integrationName={syncConfigModal.integrationName}
			/>

			{/* Sync Dashboard Modal (sync on-demand, jobs, history, mappings) */}
			<SyncDashboardModal
				open={syncDashboardModal.visible}
				onClose={() => setSyncDashboardModal({ visible: false, integrationId: 0, integrationName: "" })}
				integrationId={syncDashboardModal.integrationId}
				integrationName={syncDashboardModal.integrationName}
			/>
		</div>
	);
};

export { IntegrationsTab };
